import type { ReactNode } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import type { translations } from '@/translations/translations';
import type { ExcelPhraseRow } from './excel-phrase.types';

interface Props {
  copy: typeof translations.fr.admin.excelPhrases;
  rows: ExcelPhraseRow[];
  selectedKeys: Set<string>;
  onToggle: (key: string) => void;
  onToggleAll: () => void;
  /** How much each phrase was recorded, under its text. */
  renderStats?: (row: ExcelPhraseRow) => ReactNode;
}

export function ExcelPhraseList({ copy, rows, selectedKeys, onToggle, onToggleAll, renderStats }: Props) {
  const isAllSelected = rows.length > 0 && rows.every((row) => selectedKeys.has(row.key));

  return (
    <div className="overflow-hidden rounded-xl border border-border/50 bg-background">
      <div className="flex items-center gap-3 border-b border-border/50 bg-muted/30 px-4 py-3">
        <Checkbox checked={isAllSelected} onCheckedChange={onToggleAll} aria-label={copy.selectAllVisible} />
        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">{copy.selectAllVisible}</span>
      </div>
      <ul>
        {rows.map((row) => (
          <li key={row.key} className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3 border-b border-border/40 px-4 py-3 last:border-0 hover:bg-muted/20">
            <Checkbox checked={selectedKeys.has(row.key)} onCheckedChange={() => onToggle(row.key)} aria-label={copy.selectPhrase} className="mt-0.5" />
            <div className="min-w-0">
              <p className="break-words text-sm font-medium text-foreground">{row.local || '—'}</p>
              <p className="mt-1 break-words text-xs leading-5 text-muted-foreground">
                {row.fr}
                <span className="ml-2 inline-block break-all rounded-full bg-muted px-2 py-0.5 font-mono">
                  {row.id ? `${copy.phraseId}: ${row.id}` : copy.phraseIdMissing}
                </span>
                <span className="ml-2 rounded-full bg-muted px-2 py-0.5">
                  {row.domain || copy.domainMissing}
                </span>
              </p>
              {renderStats && <div className="mt-2">{renderStats(row)}</div>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
